/**
 * Presentation of the invoicing status vocabularies (SPEC-IVC, ALL-5908).
 *
 * The contract carries several independent status axes and a page must never
 * render a raw wire value. This module is the one place that turns each wire
 * value into a human label and a tone from the design system's `Tone`
 * vocabulary (`src/components/ui/status.tsx`), so two pages cannot label the
 * same state differently.
 *
 * The maps are the contract's enums, in the contract's order:
 *
 *   - `DocumentStatus`  — `invoicing-issuer.openapi.yaml`, LLD 3.1
 *   - `PaymentStatus`   — same, derived by `PaymentStatus.derive`, LLD 3.2
 *   - `PaymentState`    — same, the hot-wallet deposit lifecycle, LLD 3.3
 *   - `ExceptionType`   — same, raised by LLD 4
 *   - `ExceptionState`  — same
 *   - `ResolutionKind`  — same
 *   - `ResolutionState` — same
 *   - `RefundState`     — same, the pipeline in LLD 7
 *
 * Every map is a `Record<Enum, …>`, so adding a member to an enum in `types.ts`
 * is a compile error here until it is given a label — a new state can never
 * silently render as "Unknown" in production. The lookup functions additionally
 * accept any `string`, because a wire value can outrun the checked-in types
 * (the service ships first): an unrecognised value, `null`, `undefined` and the
 * empty string all render as one neutral "Unknown", never a throw and never a
 * raw value echoed into the page.
 *
 * Nothing here decides authority. A tone describes a state; it never gates an
 * action — the service decides what an actor may do (see `api.ts`).
 */

import type { Tone } from "@/components/ui/status";
import type {
  DocumentStatus,
  ExceptionState,
  ExceptionType,
  PaymentState,
  PaymentStatus,
  RefundState,
  ResolutionKind,
  ResolutionState,
} from "./types";

/** A label to render and the tone to render it in. */
export interface StatusPresentation {
  label: string;
  tone: Tone;
}

/**
 * What an unrecognised, missing or empty value renders as. Deliberately neutral
 * and deliberately not the raw value: showing `"partially_paid_v2"` or an empty
 * chip would both be worse than admitting we do not know this state.
 */
export const UNKNOWN_STATUS: StatusPresentation = { label: "Unknown", tone: "neutral" };

/* ------------------------------------------------------ document status */

/**
 * LLD 3.1. `draft` is not yet a document (nothing has been sent or numbered),
 * so it is pending rather than informative. `void` is a deliberate withdrawal
 * and `closed` is terminal and settled — neither wants an operator's attention,
 * so both are neutral. `expired` is the one document state that means money is
 * outstanding past its window, so it warns.
 */
export const DOCUMENT_STATUS = {
  draft: { label: "Draft", tone: "pending" },
  issued: { label: "Issued", tone: "info" },
  void: { label: "Void", tone: "neutral" },
  expired: { label: "Expired", tone: "warning" },
  closed: { label: "Closed", tone: "neutral" },
} satisfies Record<DocumentStatus, StatusPresentation>;

/* ------------------------------------------------------- payment status */

/**
 * LLD 3.2, in the priority `derive` applies. `paid` is the only clean success.
 * `confirming` is money on its way (pending); `partially_paid`, `overpaid` and
 * `under_review` all need a human to look, so they warn; `overdue` is the one
 * that means nothing has been received past the due date, so it is danger.
 * `paid_late` is the stored late flag on a settled invoice: money did arrive,
 * but the lateness is exactly what a collections view is looking for, so it
 * warns rather than reads as a clean success. `refunded` and `written_off` are
 * settled outcomes that returned or gave up the money, so they are neutral.
 */
export const PAYMENT_STATUS = {
  unpaid: { label: "Unpaid", tone: "neutral" },
  partially_paid: { label: "Partially paid", tone: "warning" },
  confirming: { label: "Confirming", tone: "pending" },
  under_review: { label: "Under review", tone: "warning" },
  paid: { label: "Paid", tone: "success" },
  overpaid: { label: "Overpaid", tone: "warning" },
  paid_late: { label: "Paid late", tone: "warning" },
  overdue: { label: "Overdue", tone: "danger" },
  refund_pending: { label: "Refund pending", tone: "pending" },
  refunded: { label: "Refunded", tone: "neutral" },
  written_off: { label: "Written off", tone: "neutral" },
} satisfies Record<PaymentStatus, StatusPresentation>;

/* -------------------------------------------------------- payment state */

/**
 * LLD 3.3, the individual deposit leg. `applied` is the only state where the
 * money did what the invoice needed. `held` and `rejected` are screening
 * outcomes: held is a review a person must finish (warning), rejected is funds
 * ring-fenced with no path forward but a four-eyes refund (danger). `reversed`
 * is a post-credit reorg with a reversing entry (danger). `detected` awaits
 * finality (pending); `final` and `cleared` are confirmed and awaiting
 * application (info). `unapplied` is money we hold but have not applied, which
 * always has an exception behind it (warning). `dropped` and `dust` are
 * recorded, harmless and inert (neutral).
 */
export const PAYMENT_STATE = {
  detected: { label: "Detected", tone: "pending" },
  dropped: { label: "Dropped", tone: "neutral" },
  final: { label: "Final", tone: "info" },
  cleared: { label: "Cleared", tone: "info" },
  held: { label: "Held", tone: "warning" },
  rejected: { label: "Rejected", tone: "danger" },
  unapplied: { label: "Unapplied", tone: "warning" },
  applied: { label: "Applied", tone: "success" },
  dust: { label: "Dust", tone: "neutral" },
  reversed: { label: "Reversed", tone: "danger" },
  refunded: { label: "Refunded", tone: "neutral" },
} satisfies Record<PaymentState, StatusPresentation>;

/* ------------------------------------------------------ exception type */

/**
 * LLD 4. Every type except a price deviation is money that is not where it
 * should be: `WRONG_NETWORK` and `REORG_AFTER_APPLY` need real work (danger),
 * the rest warn. `DEPEG` is informational by design — it opens an exception and
 * never blocks application — so it is info, not a warning.
 */
export const EXCEPTION_TYPE = {
  OVERPAYMENT: { label: "Overpayment", tone: "warning" },
  UNDERPAYMENT_AT_EXPIRY: { label: "Underpayment at expiry", tone: "warning" },
  UNAPPLIED_WRONG_ASSET: { label: "Unapplied — wrong asset", tone: "warning" },
  UNAPPLIED_NOT_PAYABLE: { label: "Unapplied — invoice not payable", tone: "warning" },
  WRONG_NETWORK: { label: "Wrong network", tone: "danger" },
  REORG_AFTER_APPLY: { label: "Reorg after apply", tone: "danger" },
  DEPEG: { label: "Price deviation", tone: "info" },
} satisfies Record<ExceptionType, StatusPresentation>;

/* ----------------------------------------------------- exception state */

/** `RESOLVED` is the only state that closes an exception; a cancellation is inert. */
export const EXCEPTION_STATE = {
  OPEN: { label: "Open", tone: "warning" },
  RESOLUTION_PROPOSED: { label: "Resolution proposed", tone: "pending" },
  RESOLVED: { label: "Resolved", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
} satisfies Record<ExceptionState, StatusPresentation>;

/* ------------------------------------------------------ resolution kind */

/**
 * A kind is a category, not a health state, so every kind is neutral: what the
 * resolution is doing is the state's job to convey, and colouring a `WRITE_OFF`
 * as a warning would editorialise a business decision the page is only
 * reporting. `KEEP_AS_CREDIT` is spelled out because "Keep as credit" is the
 * business term, not "Credit".
 */
export const RESOLUTION_KIND = {
  REFUND: { label: "Refund", tone: "neutral" },
  APPLY: { label: "Apply to invoice", tone: "neutral" },
  KEEP_AS_CREDIT: { label: "Keep as credit", tone: "neutral" },
  WRITE_OFF: { label: "Write off", tone: "neutral" },
} satisfies Record<ResolutionKind, StatusPresentation>;

/* ----------------------------------------------------- resolution state */

/**
 * LLD 6. `EXECUTED` is the only state where the effect happened. `APPROVED` is
 * transient (the effect runs in the same transaction as the approval, so a page
 * rarely sees it), which is why it is info rather than success. A rejection or
 * a withdrawal needs nothing further, so both are neutral; a `FAILED` execution
 * reopened the exception and does need a person, so it is danger.
 */
export const RESOLUTION_STATE = {
  PROPOSED: { label: "Proposed", tone: "pending" },
  APPROVED: { label: "Approved", tone: "info" },
  REJECTED: { label: "Rejected", tone: "neutral" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  EXECUTED: { label: "Executed", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
} satisfies Record<ResolutionState, StatusPresentation>;

/* ---------------------------------------------------------- refund state */

/**
 * LLD 7. The three waiting states are normal pipeline stages with an expected
 * next actor, not problems (pending). `READY`, `SUBMITTED` and `BROADCAST` are
 * progress (info). `BLOCKED` is the one outcome that needs a new destination
 * rather than a retry, and `FAILED` needs a reopened exception — both danger.
 */
export const REFUND_STATE = {
  AWAITING_ADDRESS: { label: "Awaiting address", tone: "pending" },
  AWAITING_CONFIRMATION: { label: "Awaiting confirmation", tone: "pending" },
  SCREENING: { label: "Screening", tone: "pending" },
  READY: { label: "Ready", tone: "info" },
  SUBMITTED: { label: "Submitted", tone: "info" },
  BROADCAST: { label: "Broadcast", tone: "info" },
  COMPLETED: { label: "Completed", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
  BLOCKED: { label: "Blocked", tone: "danger" },
} satisfies Record<RefundState, StatusPresentation>;

/* ----------------------------------------------------------- lookups */

/**
 * `Map` rather than an object index on purpose: an object lookup would happily
 * answer `constructor` or `toString` with a function off the prototype, and a
 * wire value that happens to be one of those names must still render as
 * Unknown.
 */
function index<K extends string>(
  source: Record<K, StatusPresentation>,
): (value: string | null | undefined) => StatusPresentation {
  const map = new Map<string, StatusPresentation>(Object.entries(source) as [string, StatusPresentation][]);
  return (value) => {
    if (value === null || value === undefined || value === "") return UNKNOWN_STATUS;
    return map.get(value) ?? UNKNOWN_STATUS;
  };
}

/** Label and tone for a document status (`ivc_invoices.document_status`). */
export const documentStatusPresentation = index(DOCUMENT_STATUS);

/** Label and tone for a payment status (`ivc_invoices.payment_status`). */
export const paymentStatusPresentation = index(PAYMENT_STATUS);

/** Label and tone for one payment's state (`ivc_payments.state`). */
export const paymentStatePresentation = index(PAYMENT_STATE);

/** Label and tone for an exception's type. */
export const exceptionTypePresentation = index(EXCEPTION_TYPE);

/** Label and tone for an exception's state. */
export const exceptionStatePresentation = index(EXCEPTION_STATE);

/** Label and tone for a resolution's kind. */
export const resolutionKindPresentation = index(RESOLUTION_KIND);

/** Label and tone for a resolution's state. */
export const resolutionStatePresentation = index(RESOLUTION_STATE);

/** Label and tone for a refund's state. */
export const refundStatePresentation = index(REFUND_STATE);
